import { Capacitor } from '@capacitor/core'
import { log } from './logger'
import { canReloadAgain, isChunkLoadError } from './chunkLoadError'
import { isRestartInFlight, markRestartInFlight } from './webViewRestart'

// sessionStorage: per tab, and survives the reload.
const LAST_RELOAD_KEY = 'chunk_reload_at'

function readLastReload(): number | null {
  const last = Number(sessionStorage.getItem(LAST_RELOAD_KEY) ?? NaN)
  return Number.isFinite(last) ? last : null
}

export function reloadForStaleChunk(error: unknown): boolean {
  if (Capacitor.isNativePlatform() || !isChunkLoadError(error)) return false
  if (isRestartInFlight()) return true

  const details = {
    message: error instanceof Error ? error.message : String(error),
    path: window.location.pathname,
  }
  try {
    if (!canReloadAgain(readLastReload(), Date.now())) {
      log('chunk-reload', 'giving up — reloaded recently', details)
      return false
    }
    sessionStorage.setItem(LAST_RELOAD_KEY, String(Date.now()))
  } catch (err) {
    log('chunk-reload', 'giving up — sessionStorage unavailable', { ...details, error: String(err) })
    return false
  }

  try {
    markRestartInFlight('chunk_reload')
  } catch {
    // The in-memory flag is already set; only the boot_kind tag is lost.
  }
  log('chunk-reload', 'reloading for stale chunk', details)
  window.location.reload()
  return true
}

// After a deploy the new SW takes over and cleanupOutdatedCaches drops the old
// precache, while this tab still runs the old bundle — its next lazy import
// points at a chunk hash that no longer exists anywhere (#595). No
// preventDefault: lazy() would then resolve to undefined and throw a misleading
// error; the original one reaches RouteErrorBoundary, which renders blank while
// the reload is in flight.
export function installStaleChunkReload() {
  window.addEventListener('vite:preloadError', e => {
    reloadForStaleChunk((e as Event & { payload?: unknown }).payload)
  })
}
